import React from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, TextField, Container, Typography, Alert, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { saveRegister } from 'src/api/blog';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: '#1e1e1e',
  padding: theme.spacing(2),
}));

const FormContainer = styled(Container)(({ theme }) => ({
  width: '100%',
  maxWidth: '400px',
  marginTop: theme.spacing(-10),
  backgroundColor: '#2e2e2e',
  padding: theme.spacing(3),
  borderRadius: '8px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  color: '#fff',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiInputBase-input': {
    color: '#fff',
  },
  '& .MuiInputLabel-root': {
    color: '#fff',
  },
  '& .MuiFormHelperText-root': {
    color: '#fff',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.5),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
  },
}));

const validationSchema = Yup.object().shape({
  employeeNo: Yup.string().required('Employee No is required'),
  password: Yup.string().required('Password is required'),
});

function RegisterForm({ userId, onRegistered }) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      employeeNo: '',
      password: '',
    },
  });

  const onSubmit = async (data) => {
    try {
      const response = await saveRegister({ ...data, userId });
      enqueueSnackbar('Register successfully', { variant: 'success' });
      onRegistered(response);
      window.location = "/dashboard/user/cardbusiness";
    } catch (error) {
      enqueueSnackbar(`Error Register: ${error.message}`, { variant: 'error' });
      reset();
    }
  };

  return (
    <Root>
      <FormContainer component="main" maxWidth="xs">
        <Title component="h1" variant="h5">
          Register Page
        </Title>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2.5}>
            {!!errors.submit && <Alert severity="error">{errors.submit.message}</Alert>}
            <Controller
              name="employeeNo"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Employee No"
                  error={!!errors.employeeNo}
                  helperText={errors.employeeNo ? errors.employeeNo.message : ''}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  type="password"
                  label="Password"
                  error={!!errors.password}
                  helperText={errors.password ? errors.password.message : ''}
                />
              )}
            />
            <StyledButton
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isSubmitting}
            >
              Register
            </StyledButton>
          </Stack>
        </form>
      </FormContainer>
    </Root>
  );
}

RegisterForm.propTypes = {
  userId: PropTypes.string.isRequired,
  onRegistered: PropTypes.func.isRequired,
};

export default RegisterForm;
